/**
 * ReauthDialog — friendly re-sign-in prompt
 *
 * Shown when the SPA detects an expired session (401 from API or null token).
 * Provides a "Sign in" primary action that calls redirectToLogin() — which
 * does a SILENT re-auth via the BrainDance central session when possible,
 * and only OTP-prompts if that session is also gone.
 *
 * Intentionally does NOT call logout(): logout clears the cross-site Cognito
 * cookie, signing the user out of every BrainDance site. A session that just
 * expired locally usually still has a valid central session — redirectToLogin
 * preserves that and re-auths transparently.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { redirectToLogin } from '@codespeak11/cognito-passwordless';

export const REAUTH_EVENT = 'auth:reauth-needed';

/** Fire from any layer (e.g. the API client) when a 401 / null-token is hit. */
export function requestReauth(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REAUTH_EVENT));
}

export function ReauthDialog() {
  const [open, setOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const onReauth = () => setOpen(true);
    window.addEventListener(REAUTH_EVENT, onReauth);
    return () => window.removeEventListener(REAUTH_EVENT, onReauth);
  }, []);

  const handleSignIn = useCallback(() => {
    setRedirecting(true);
    // Full-page redirect to the BrainDance portal. Returns to current path
    // (preserved as ?returnTo=...) after the callback flow.
    redirectToLogin();
  }, []);

  const handleDismiss = useCallback(() => {
    if (!redirecting) setOpen(false);
  }, [redirecting]);

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <DialogTitle>Sign in to continue</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your cxFab session expired. Sign in again to pick up where you
          left off — you&apos;ll stay signed in to other BrainDance sites.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} disabled={redirecting}>
          Not now
        </Button>
        <Button
          onClick={handleSignIn}
          variant="contained"
          startIcon={<LoginIcon />}
          disabled={redirecting}
          autoFocus
        >
          Sign in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
